import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, ArrowLeft, Plus, Newspaper, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import TurndownService from "turndown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const turndown = new TurndownService({ headingStyle: "atx", bulletListMarker: "-" });
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { NotificationBell } from "../components/site/NotificationBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return "—"; } };
const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const EMPTY_FORM = { title: "", slug: "", excerpt: "", meta_description: "", tags: "", body: "", status: "draft" };

export default function AdminBlog() {
  const { token, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // post being edited, null = new
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [cover, setCover] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!authLoading && (!token || role !== "admin")) navigate("/admin/login");
  }, [authLoading, token, role, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`, authHeader);
      setPosts(data.posts || []);
    } catch { toast.error("Could not load blog posts."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token && role === "admin") load(); }, [token, role, load]);

  const openNew = () => {
    setEditing(null); setForm(EMPTY_FORM); setSlugTouched(false); setCover(null); setShowPreview(false); setEditorOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title, slug: p.slug, excerpt: p.excerpt || "", meta_description: p.meta_description || "",
      tags: (p.tags || []).join(", "), body: p.body || "", status: p.status,
    });
    setSlugTouched(true); setCover(null); setShowPreview(false); setEditorOpen(true);
  };

  const save = async () => {
    if (form.title.trim().length < 3) { toast.error("Title needs at least 3 characters."); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title, body: form.body, excerpt: form.excerpt,
        meta_description: form.meta_description,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        status: form.status, slug: form.slug || undefined,
      };
      let post;
      if (editing) {
        ({ data: { post } } = await axios.patch(`${API}/admin/blog/posts/${editing.id}`, payload, authHeader));
        toast.success("Post updated.");
      } else {
        ({ data: { post } } = await axios.post(`${API}/admin/blog/posts`, payload, authHeader));
        toast.success(form.status === "published" ? "Post published." : "Draft saved.");
      }
      if (cover && post) {
        const fd = new FormData();
        fd.append("file", cover);
        await axios.post(`${API}/admin/blog/posts/${post.id}/cover`, fd, authHeader);
      }
      setEditorOpen(false);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save the post.");
    } finally { setBusy(false); }
  };

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API}/admin/blog/posts/${p.id}`, { status: next }, authHeader);
      toast.success(next === "published" ? "Post published." : "Post unpublished (back to draft).");
      await load();
    } catch { toast.error("Could not change status."); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${p.id}`, authHeader);
      toast.success("Post deleted.");
      await load();
    } catch { toast.error("Could not delete the post."); }
  };

  // Pasting rich text (e.g. from Google Docs) keeps links/headings/bold — converted to markdown.
  const handleBodyPaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    if (!html) return; // plain-text paste behaves normally
    const mdText = turndown.turndown(html);
    e.preventDefault();
    const el = e.target;
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? form.body.length;
    setForm((f) => ({ ...f, body: f.body.slice(0, start) + mdText + f.body.slice(end) }));
    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = start + mdText.length; });
  };

  if (authLoading || loading) {
    return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#EAF7FC]">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button data-testid="nav-back-dashboard" onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#54BCE6] transition-colors">
              <ArrowLeft size={16} /> <span className="hidden sm:inline">Dashboard</span>
            </button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#9b1c1c] hover:text-[#9b1c1c] transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#133955] flex items-center gap-2.5"><Newspaper size={24} /> Blog</h1>
            <p className="text-sm text-[#173042]/60 mt-1">{posts.length} post{posts.length === 1 ? "" : "s"} — published posts appear at <a href="/blog" target="_blank" rel="noreferrer" className="text-[#54BCE6] font-semibold hover:underline">/blog</a></p>
          </div>
          <button data-testid="blog-new-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors">
            <Plus size={16} /> New Post
          </button>
        </div>

        {posts.length === 0 ? (
          <div className="rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-10 text-center" data-testid="blog-admin-empty">
            <p className="font-bold text-[#133955]">No posts yet</p>
            <p className="text-sm text-[#173042]/60 mt-1">Write your first post — homeowners find these through Google.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="blog-admin-list">
            {posts.map((p) => (
              <div key={p.id} className="rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-5 flex items-center gap-4 flex-wrap" data-testid={`blog-row-${p.id}`}>
                {p.cover_file && <img src={`${API}/files/${p.cover_file.file_id}`} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#133955]">{p.title}</span>
                    <span data-testid={`blog-status-${p.id}`} className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${p.status === "published" ? "bg-[#C7F0D8] text-[#0f6b3d]" : "bg-[#FFE8B0] text-[#7a5b00]"}`}>{p.status === "published" ? "Published" : "Draft"}</span>
                  </div>
                  <p className="text-xs text-[#173042]/60 mt-1">
                    <span className="font-mono text-[#54BCE6]">/blog/{p.slug}</span>
                    {" · "}{p.status === "published" ? `Published ${fmt(p.published_at)}` : `Updated ${fmt(p.updated_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {p.status === "published" && (
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`blog-view-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-3.5 py-1.5 text-xs font-bold text-[#133955] hover:border-[#54BCE6] transition-colors">
                      <ExternalLink size={13} /> View
                    </a>
                  )}
                  <button data-testid={`blog-toggle-${p.id}`} onClick={() => toggleStatus(p)} className="inline-flex items-center gap-1.5 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-3.5 py-1.5 text-xs font-bold text-[#133955] hover:border-[#54BCE6] transition-colors">
                    {p.status === "published" ? <><EyeOff size={13} /> Unpublish</> : <><Eye size={13} /> Publish</>}
                  </button>
                  <button data-testid={`blog-edit-${p.id}`} onClick={() => openEdit(p)} className="inline-flex items-center gap-1.5 rounded-full bg-[#133955] text-white px-3.5 py-1.5 text-xs font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors">
                    <Pencil size={13} /> Edit
                  </button>
                  <button data-testid={`blog-delete-${p.id}`} onClick={() => remove(p)} className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#9b1c1c]/30 text-[#9b1c1c] px-3.5 py-1.5 text-xs font-bold hover:bg-[#FBD5D5] transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-editor">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Post" : "New Post"}</DialogTitle>
            <DialogDescription>Write with markdown: **bold**, ## headings, - bullet lists, [links](https://…). Pasting from Google Docs keeps your links and formatting automatically. Published posts appear on the public blog and in the sitemap.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Title *</label>
              <input
                data-testid="blog-form-title"
                className="hpc-input bg-white"
                value={form.title}
                onChange={(e) => {
                  const t = e.target.value;
                  setForm((f) => ({ ...f, title: t, ...(slugTouched ? {} : { slug: slugify(t) }) }));
                }}
                placeholder="e.g. 5 Questions to Ask Before Hiring a Roofer"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">URL Slug</label>
                <input
                  data-testid="blog-form-slug"
                  className="hpc-input bg-white font-mono text-sm"
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }}
                  placeholder="auto-from-title"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Tags (comma separated)</label>
                <input data-testid="blog-form-tags" className="hpc-input bg-white" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="Roofing, Hiring Tips" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Excerpt</label>
              <textarea data-testid="blog-form-excerpt" className="hpc-input bg-white" rows={2} value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} placeholder="One or two sentences shown on the blog list." />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Search Description (Google)</label>
              <textarea data-testid="blog-form-meta" className="hpc-input bg-white" rows={2} maxLength={200} value={form.meta_description} onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))} placeholder="150–160 characters shown in search results. Falls back to the excerpt." />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Cover Photo (optional)</label>
              <div className="flex items-center gap-3 flex-wrap">
                <input data-testid="blog-form-cover" type="file" accept="image/*" onChange={(e) => setCover(e.target.files?.[0] || null)} className="text-sm text-[#173042]/70" />
                {editing?.cover_file && !cover && (
                  <span className="text-xs text-[#173042]/60 flex items-center gap-2">
                    Current: <img src={`${API}/files/${editing.cover_file.file_id}`} alt="current cover" className="w-10 h-10 rounded-lg object-cover" />
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold text-[#133955] uppercase tracking-wide">Body (markdown)</label>
                <button type="button" data-testid="blog-form-preview-toggle" onClick={() => setShowPreview((v) => !v)} className="text-xs font-bold text-[#54BCE6] hover:text-[#133955]">
                  {showPreview ? "Back to editor" : "Preview"}
                </button>
              </div>
              {showPreview ? (
                <div className="rounded-xl border border-[rgba(19,57,85,0.12)] bg-white p-5 min-h-[240px]" data-testid="blog-form-preview">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body || "*Nothing to preview yet.*"}</ReactMarkdown>
                </div>
              ) : (
                <textarea
                  data-testid="blog-form-body"
                  className="hpc-input bg-white font-mono text-sm"
                  rows={12}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  onPaste={handleBodyPaste}
                  placeholder={"## Start with a heading\n\nWrite your post here…\n\n- Bullet points\n- work great"}
                />
              )}
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-[#133955]">
                <input
                  type="checkbox"
                  data-testid="blog-form-publish"
                  checked={form.status === "published"}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? "published" : "draft" }))}
                  className="w-4 h-4 accent-[#133955]"
                />
                Publish immediately
              </label>
              <button data-testid="blog-save-btn" onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-3 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50">
                {busy && <Loader2 size={15} className="animate-spin" />}
                {editing ? "Save Changes" : "Create Post"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
